import React, { useState } from "react"
import { Flex } from "antd"
import { useTranslation } from "react-i18next"
import DangerActionCard from "./DangerActionCard"
import { spacings } from "themes/variables"
import { useSingleModal } from "./hooks/useSingleModal"
import { useConfirmModalContext } from "./hooks/useConfirmModalContext"
import DeleteAccountWarning from "./modals/DeleteAccountWarning"
import {
  DELETE_ACCOUNT_CONFIRMATION,
  ACCOUNT_STATUS_WARNING,
  DELETE_ACCOUNT_WARNING,
  SCHEDULE_DELETION,
  DELETE_ACCOUNT_UNAVAILABLE,
  ACCOUNT_STATUS_CONFIRMATION,
  CANCEL_DELETION,
  DEACTIVATED
} from "./constants"
import ConfirmAccountDeletionModal from "./modals/ConfirmAccountDeletion"
import ScheduleDeletionModal from "./modals/ScheduleDeletion"
import { useDispatch, useSelector } from "react-redux"
import AccountStatusPrompt from "./modals/AccountStatusPrompt"
import DeletionUnavailable from "./modals/DeletionUnavailable"
import { accountActions } from "./actions"
import { sendOtpForAccountAction, sendOtpForAccountActionLoadingSelector } from "store/user"
import moment from "moment"
import { toaster } from "components/common/Toast"


const getScheduledDeletionDescriptionText = (t, scheduledDeletionDate, isDeactivated) => {
  if (scheduledDeletionDate && isDeactivated) {
    return t("settings.scheduled_deletion_notice_description_deactivated")
  } else if (scheduledDeletionDate && !isDeactivated) {
    return t("settings.scheduled_deletion_notice_description")
  }
  return t("settings.delete_account_description")
}

function openDeleteFlowHelper({ open, scheduledDeletionDate, dispatch }) {
  return async () => {
    if (scheduledDeletionDate) {
      const result = await dispatch(sendOtpForAccountAction())
      if (result?.message) {
        toaster.success(result?.message)
      }
      open(CANCEL_DELETION)
    } else {
      open(DELETE_ACCOUNT_WARNING)
    }
  }
}

function renderModals({ isOpen, propsFor, handleDeleteAccountWarning, showConfirmModal, confirmCtx, confirmProps, handleConfirmByUser, proceedToScheduleDeletion, selected, setSelected, handleDeletionUnavailable, isDeactivated, t, proceedToAccountStatusConfirmation, isSendingOtp }) {
  return <>
    {isOpen(DELETE_ACCOUNT_WARNING) && (
      <DeleteAccountWarning primaryColor="danger" {...propsFor(DELETE_ACCOUNT_WARNING)} onDelete={handleDeleteAccountWarning} />
    )}
    {showConfirmModal && <ConfirmAccountDeletionModal {...confirmCtx} {...confirmProps} onConfirm={handleConfirmByUser} />}
    {isOpen(SCHEDULE_DELETION) && (
      <ScheduleDeletionModal
        primaryColor="danger"
        onProceed={proceedToScheduleDeletion}
        selected={selected}
        setSelected={setSelected}
        {...propsFor(SCHEDULE_DELETION)}
      />
    )}
    {isOpen(ACCOUNT_STATUS_WARNING) && (
      <AccountStatusPrompt
        text={isDeactivated ? t("settings.activate_account_description") : t("settings.deactivate_account_description")}
        title={isDeactivated ? t("settings.activate_account") : t("settings.deactivate_account")}
        primaryLabel={isDeactivated ? t("settings.activate_account") : t("settings.deactivate_account")}
        onProceed={proceedToAccountStatusConfirmation}
        primaryColor={isDeactivated ? "primary" : "danger"}
        primaryLoading={isSendingOtp}
        primaryDisabled={isSendingOtp}
        {...propsFor(ACCOUNT_STATUS_WARNING)}
      />
    )}
    {isOpen(DELETE_ACCOUNT_UNAVAILABLE) && (
      <DeletionUnavailable {...propsFor(DELETE_ACCOUNT_UNAVAILABLE)} onDeactivate={handleDeletionUnavailable} />
    )}
  </>
}

export const AccountManagement = () => {
  const [selected, setSelected] = useState("active")
  const [isScheduleDeletion, setIsScheduleDeletion] = useState(false)
  const { account_status, scheduled_deletion_date, verification_status } = useSelector((state) => state.user?.guide)
  const isSendingOtp = useSelector(sendOtpForAccountActionLoadingSelector)
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const isDeactivated = account_status === DEACTIVATED
  const scheduledDeletionDate = scheduled_deletion_date ? moment(scheduled_deletion_date).format("MMMM D, YYYY") : ""
  const { open, close, propsFor, isOpen } = useSingleModal(null)
  const { showConfirmModal, confirmCtx, confirmProps } = useConfirmModalContext({
    isDeactivated,
    isOpen,
    propsFor,
    t,
    scheduledDeletionDate,
    isScheduleDeletion
  })
  const isDeleteDisabled = isDeactivated && !scheduledDeletionDate
  const isVerificationApproved = verification_status === "approved"

  const handleDeleteAccountWarning = async () => {
    await accountActions.deleteAccountWarning({ dispatch, open, setIsScheduleDeletion })
  }

  const proceedToAccountStatusConfirmation = async () => {
    try {
      const result = await dispatch(sendOtpForAccountAction())
      if (result) {
        toaster.success(result.message)
        open(ACCOUNT_STATUS_CONFIRMATION)
      }
    } catch (error) {
      console.error(error)
    }
  }

  const handleDeletionUnavailable = () => {
    close()
  }

  const handleConfirmByUser = async ({ otp }) => {
    const isDeactivateAction = isOpen(ACCOUNT_STATUS_CONFIRMATION)
    // Convert OTP array to string
    const otpString = Array.isArray(otp) ? otp.join("") : otp
    await accountActions.handleConfirmByUser({
      dispatch,
      isDeactivateAction,
      otp: otpString,
      isDeactivated,
      selected,
      scheduledDeletionDate,
      isScheduleDeletion
    })
    close()
  }

  const handleDeactivateAccount = () => {
    open(ACCOUNT_STATUS_WARNING)
  }

  const handleDeleteActions = openDeleteFlowHelper({ open, scheduledDeletionDate, dispatch })

  const proceedToScheduleDeletion = async () => {
    const result = await dispatch(sendOtpForAccountAction())
    if (result) {
      toaster.success(result.message)
      open(DELETE_ACCOUNT_CONFIRMATION)
    }
  }

  return (
    <>
      <Flex gap={spacings.M} vertical>
        <DangerActionCard
          disabled={scheduledDeletionDate || !isVerificationApproved}
          title={isDeactivated ? t("settings.deactivate_profile.banner_title") : t("settings.deactivate_account")}
          description={isDeactivated ? t("settings.deactivate_profile.banner_description") : t("settings.temporary_deactivation")}
          buttonTitle={isDeactivated ? t("settings.deactivate_profile.activate_account") : t("settings.deactivate_account")}
          buttonVariant={isDeactivated ? "solid" : "outlined"}
          onClick={scheduledDeletionDate || !isVerificationApproved ? undefined : handleDeactivateAccount}
          color={isDeactivated ? "primary" : "danger"}
        />
        <DangerActionCard
          title={
            scheduledDeletionDate ? t("settings.scheduled_deletion_notice_title", { scheduledDeletionDate }) : t("settings.delete_account")
          }
          description={getScheduledDeletionDescriptionText(t, scheduledDeletionDate, isDeactivated)}
          buttonTitle={scheduledDeletionDate ? t("settings.cancel_deletion") : t("settings.delete_account")}
          buttonVariant="solid"
          onClick={isDeleteDisabled ? undefined : handleDeleteActions}
          color={scheduledDeletionDate ? "primary" : "danger"}
          disabled={isDeleteDisabled}
        />
      </Flex>
      {renderModals({
        isOpen,
        propsFor,
        handleDeleteAccountWarning,
        showConfirmModal,
        confirmCtx,
        confirmProps,
        handleConfirmByUser,
        proceedToScheduleDeletion,
        selected,
        setSelected,
        handleDeletionUnavailable,
        isDeactivated,
        t,
        proceedToAccountStatusConfirmation,
        isSendingOtp
      })}
    </>
  )
}
